/**
 * assets/handoffs/design_handoff_workspace_members — Members tab rebuild
 * onto Untitled UI. See OrganizationMembersPage/ for the card, dialogs,
 * row menu and toast.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAppStore } from "./../../../hooks/redux/app";
import TabbedSettingsPage from "../templates/TabbedSettingsPage/TabbedSettingsPage";
import { WORKSPACE_SETTINGS_TABS } from "../templates/TabbedSettingsPage/settingsTabs";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useOrganizationMembersStore } from "../../../hooks/redux/organizationMembers";
import { useUserStore } from "../../../hooks/redux/user";
import { hasSubscription } from "../helpers/organization";
import { QUOTAS, UsageFieldsMapping } from "../../../consts";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import MembersCard from "./OrganizationMembersPage/MembersCard";
import InviteDialog from "./OrganizationMembersPage/InviteDialog";
import RemoveMemberDialog from "./OrganizationMembersPage/RemoveMemberDialog";
import RolePermissionsDialog from "./OrganizationMembersPage/RolePermissionsDialog";
import MemberRemovedToast from "./OrganizationMembersPage/MemberRemovedToast";
import {
  INVITE_STATUS_ACCEPTED,
  INVITE_STATUS_PENDING,
  ROLE_OWNER,
} from "./OrganizationMembersPage/constants";
import { parseGeneralError } from "./OrganizationMembersPage/errorHelpers";

const initials = (row) => {
  if (row.status === "pending") return null;
  const first = row.firstName?.[0] || "";
  const last = row.lastName?.[0] || "";
  const combined = (first + last).toUpperCase();
  return combined || row.email?.[0]?.toUpperCase() || "?";
};

const toRow = (raw, myUuid) => {
  const accepted = raw.invite_status === INVITE_STATUS_ACCEPTED;
  const user = raw.user || {};
  const firstName = user.first_name || "";
  const lastName = user.last_name || "";
  const name = accepted
    ? `${firstName} ${lastName}`.trim() || user.email
    : raw.invite_email;

  const row = {
    uuid: raw.uuid,
    role: raw.role,
    status: accepted ? "active" : "pending",
    name,
    email: accepted ? user.email : null,
    firstName,
    lastName,
    isCurrentUser: accepted && !!myUuid && user.uuid === myUuid,
    lastLogin: user.last_login || null,
    createdAt: raw.created || null,
  };
  return { ...row, initials: initials(row) };
};

// useToast() needs a ToastProvider ancestor, which can't be the same
// component instance that calls the hook — so the actual page content
// lives in this inner component, wrapped by the default export below.
const OrganizationMembersPageContent = () => {
  const { clearErrors } = useAppStore();
  const showToast = useToast();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { user } = useUserStore();
  const {
    organizationMembers,
    fetch,
    addOrganizationMember,
    editOrganizationMember,
    deleteOrganizationMember,
    reinviteOrganizationMember,
  } = useOrganizationMembersStore();

  const orgModel = currentOrganization.model || {};
  const myUuid = user.model?.uuid;

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [busyRowId, setBusyRowId] = useState(null);
  const [rowError, setRowError] = useState(null); // {rowId, message}
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // row
  const [rolePermissionsOpen, setRolePermissionsOpen] = useState(false);
  const [toast, setToast] = useState(null); // {email}

  const organizationMembersRef = useRef(organizationMembers);
  organizationMembersRef.current = organizationMembers;

  useEffect(() => {
    if (orgModel.uuid) fetch(orgModel.uuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgModel.uuid]);

  useEffect(() => {
    return clearErrors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allRows = useMemo(
    () =>
      (organizationMembers.collection || [])
        .filter(
          (raw) =>
            raw.invite_status === INVITE_STATUS_ACCEPTED ||
            raw.invite_status === INVITE_STATUS_PENDING
        )
        .map((raw) => toRow(raw, myUuid)),
    [organizationMembers.collection, myUuid]
  );

  const ownerCount = useMemo(
    () =>
      allRows.filter((r) => r.status === "active" && r.role === ROLE_OWNER)
        .length,
    [allRows]
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((row) => {
      if (roleFilter !== "all" && row.role !== roleFilter) {
        return false;
      }
      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }
      if (q) {
        const haystack = `${row.name} ${row.email || ""}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allRows, roleFilter, statusFilter, query]);

  const memberCount = allRows.filter((r) => r.status === "active").length;
  const pendingCount = allRows.filter((r) => r.status === "pending").length;

  const usage = Number(orgModel.usage?.[UsageFieldsMapping[QUOTAS.team]] || 0);
  const subscription = hasSubscription(currentOrganization);
  const seatLimit = subscription?.quotas
    ? Number(subscription.quotas[QUOTAS.team])
    : null;

  const isFiltered =
    query.trim() !== "" || roleFilter !== "all" || statusFilter !== "all";

  const onSearchChange = useCallback((e) => setQuery(e.target.value), []);
  const onRoleFilterChange = useCallback((value) => setRoleFilter(value), []);
  const onStatusFilterChange = useCallback(
    (value) => setStatusFilter(value),
    []
  );
  const onClearFilters = useCallback(() => {
    setQuery("");
    setRoleFilter("all");
    setStatusFilter("all");
  }, []);

  const onDismissRowError = useCallback(() => setRowError(null), []);

  const runRowAction = useCallback(async (rowUuid, action) => {
    setBusyRowId(rowUuid);
    setRowError(null);
    await action();
    setBusyRowId(null);
  }, []);

  const onChangeRole = useCallback(
    async (row, nextRole) => {
      if (row.role === nextRole) return;
      await runRowAction(row.uuid, () =>
        editOrganizationMember(row.uuid, { role: nextRole })
      );
      const latest = organizationMembersRef.current;
      if (latest.editError) {
        setRowError({
          rowId: row.uuid,
          message: parseGeneralError(
            latest.editError,
            "The service didn't respond. Try again in a moment."
          ),
          retry: () => onChangeRole(row, nextRole),
        });
      } else {
        fetch(orgModel.uuid);
      }
    },
    [editOrganizationMember, fetch, orgModel.uuid, runRowAction]
  );

  const onResendInvite = useCallback(
    async (row) => {
      await runRowAction(row.uuid, () => reinviteOrganizationMember(row.uuid));
      const latest = organizationMembersRef.current;
      if (latest.resendInvitationError) {
        setRowError({
          rowId: row.uuid,
          message: parseGeneralError(
            latest.resendInvitationError,
            "The service didn't respond. Try again in a moment."
          ),
          retry: () => onResendInvite(row),
        });
      } else {
        showToast({
          variant: "success",
          message: `Invitation resent to ${row.name}.`,
        });
      }
    },
    [reinviteOrganizationMember, runRowAction, showToast]
  );

  const onRevokeInvite = useCallback(
    async (row) => {
      await runRowAction(row.uuid, () => deleteOrganizationMember(row.uuid));
      const latest = organizationMembersRef.current;
      if (latest.deleteError) {
        setRowError({
          rowId: row.uuid,
          message: parseGeneralError(
            latest.deleteError,
            "The service didn't respond. Try again in a moment."
          ),
          retry: () => onRevokeInvite(row),
        });
      } else {
        fetch(orgModel.uuid);
      }
    },
    [deleteOrganizationMember, fetch, orgModel.uuid, runRowAction]
  );

  const onOpenRemove = useCallback((row) => setRemoveTarget(row), []);
  const onCloseRemove = useCallback(() => setRemoveTarget(null), []);

  const onConfirmRemove = useCallback(async () => {
    const row = removeTarget;
    if (!row) {
      return;
    }
    await runRowAction(row.uuid, () => deleteOrganizationMember(row.uuid));
    const latest = organizationMembersRef.current;
    if (latest.deleteError) {
      setRemoveTarget(null);
      setRowError({
        rowId: row.uuid,
        message: parseGeneralError(
          latest.deleteError,
          "The service didn't respond. Try again in a moment."
        ),
        retry: () => onOpenRemove(row),
      });
    } else {
      setRemoveTarget(null);
      setToast({ email: row.email });
      fetch(orgModel.uuid);
    }
  }, [
    removeTarget,
    deleteOrganizationMember,
    fetch,
    orgModel.uuid,
    runRowAction,
    onOpenRemove,
  ]);

  const onCloseToast = useCallback(() => setToast(null), []);

  const onOpenInvite = useCallback(() => setInviteOpen(true), []);
  const onCloseInvite = useCallback(() => setInviteOpen(false), []);

  const onSubmitInvite = useCallback(
    async (emails, role) => {
      for (const email of emails) {
        await addOrganizationMember({
          organization_uuid: orgModel.uuid,
          invite_email: email,
          role,
        });
      }
      fetch(orgModel.uuid);
    },
    [addOrganizationMember, fetch, orgModel.uuid]
  );

  const onOpenRolePermissions = useCallback(
    () => setRolePermissionsOpen(true),
    []
  );
  const onCloseRolePermissions = useCallback(
    () => setRolePermissionsOpen(false),
    []
  );

  const onRetryFetch = useCallback(
    () => fetch(orgModel.uuid),
    [fetch, orgModel.uuid]
  );

  const seatsFree =
    seatLimit != null && seatLimit >= 0 ? Math.max(seatLimit - usage, 0) : null;

  return (
    <TabbedSettingsPage
      title="Workspace settings"
      subtitle="Applies to this workspace and everyone in it."
      tabs={WORKSPACE_SETTINGS_TABS}
    >
      <MembersCard
        rows={rows}
        loading={organizationMembers.fetch}
        error={organizationMembers.fetchError}
        onRetryFetch={onRetryFetch}
        query={query}
        onSearchChange={onSearchChange}
        roleFilter={roleFilter}
        onRoleFilterChange={onRoleFilterChange}
        statusFilter={statusFilter}
        onStatusFilterChange={onStatusFilterChange}
        isFiltered={isFiltered}
        onClearFilters={onClearFilters}
        memberCount={memberCount}
        pendingCount={pendingCount}
        totalCount={allRows.length}
        seatsUsed={usage}
        seatLimit={seatLimit}
        seatsFree={seatsFree}
        ownerCount={ownerCount}
        busyRowId={busyRowId}
        rowError={rowError}
        onDismissRowError={onDismissRowError}
        onChangeRole={onChangeRole}
        onResendInvite={onResendInvite}
        onRevokeInvite={onRevokeInvite}
        onOpenRemove={onOpenRemove}
        onOpenInvite={onOpenInvite}
        onOpenRolePermissions={onOpenRolePermissions}
        inviteDisabled={seatsFree === 0}
      />

      <InviteDialog
        open={inviteOpen}
        onClose={onCloseInvite}
        onSubmit={onSubmitInvite}
        seatsFree={seatsFree}
        sending={organizationMembers.add}
      />

      <RemoveMemberDialog
        row={removeTarget}
        busy={!!removeTarget && busyRowId === removeTarget.uuid}
        onClose={onCloseRemove}
        onConfirm={onConfirmRemove}
      />

      <RolePermissionsDialog
        open={rolePermissionsOpen}
        onClose={onCloseRolePermissions}
      />

      {toast && (
        <MemberRemovedToast email={toast.email} onDismiss={onCloseToast} />
      )}
    </TabbedSettingsPage>
  );
};

const OrganizationMembersPage = () => (
  <ToastProvider>
    <OrganizationMembersPageContent />
  </ToastProvider>
);

export default OrganizationMembersPage;
